import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { TableCell, TableRow } from '@mui/material';
import useQuery from 'hooks/apollo/useQuery';
import useSubscription from 'hooks/apollo/useSubscription';
import { useMutation } from 'hooks/apollo/useMutation';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { getErrors, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { get, sequence, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { concatFailure, items, required, shape, unique } from '@totalsoft/pure-validations';
import { usePermission } from 'providers/RightsHooks';
import { useToast } from 'hooks/toasts/useToast';
import { useReactiveVar } from '@apollo/client';
import { isIncomeSchemaEditableVar } from '../../variables';
import { ADMIN_LEASING_INCOME_SCHEMAS_WRITE, MASTER_PARTNER_READ } from 'constants/rightsCodes';
import { env } from 'utils/env';
import FlexBox from 'components/common/FlexBox';
import MaxHeightTable from 'components/common/MaxHeightTable';
import IconButton from 'components/common/buttons/IconButton';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { ThemedWrapper } from 'components/common/SurfaceStyles';
import { RoundedHeaderTableRow, ThemedHeaderTableCell } from 'components/common/table/TableStyles';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';
import { LEASING_INCOME_SCHEMA_PARTNERS } from 'features/leasingIncomeSchema/queries';
import { SAVE_LEASING_INCOME_SCHEMA_PARTNERS } from 'features/leasingIncomeSchema/mutations';
import { LEASING_INCOME_SCHEMA_PARTNERS_SAVED } from 'features/leasingIncomeSchema/subscriptions';
import LeasingIncomeSchemaXPartnerComponent from './LeasingIncomeSchemaXPartnerComponent';

const masterPartnerPath = env.REACT_APP_MASTERPARTNER_URL;

const handleOpenMasterPartner = masterPartnerId => e => {
  e.stopPropagation();
  window.open(`${masterPartnerPath}/partners/${masterPartnerId}`, '_blank');
};

const validator = concatFailure(
  unique(x => x.partnerId || {}, 'LeasingIncomeSchema.PartnerName'),
  items(
    shape({
      partnerId: required
    })
  )
);
const LeasingIncomeSchemaXPartnersContainer = ({ id }) => {
  const { t } = useTranslation();

  const addToast = useToast();
  const canWrite = usePermission(ADMIN_LEASING_INCOME_SCHEMAS_WRITE);
  const canReadMasterPartner = usePermission(MASTER_PARTNER_READ);
  const isEditable = useReactiveVar(isIncomeSchemaEditableVar);
  const [pending, setPending] = useState(false);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens([]);
  const [validation, validate] = useDirtyFieldValidation(validator);

  const { loading } = useQuery(LEASING_INCOME_SCHEMA_PARTNERS, {
    variables: { id },
    skip: !id,
    onCompleted: data => {
      resetLens((data?.leasingIncomeSchemaXPartners || []).map(item => ({ ...item, key: crypto.randomUUID() })));
    }
  });

  const [saveLeasingIncomeSchemaXPartners] = useMutation(SAVE_LEASING_INCOME_SCHEMA_PARTNERS, {
    onError: () => {
      setPending(false);
    }
  });

  useSubscription(LEASING_INCOME_SCHEMA_PARTNERS_SAVED, {
    skip: !id,
    variables: { id },
    onData: ({ client, data }) => {
      const currentPartners = lens |> get;
      const newPartners = data?.data?.leasingIncomeSchemaXPartnersSaved?.partners || [];
      currentPartners.forEach(currentPartner => {
        const partnerToUpdate = newPartners.find(newPartner => newPartner.partnerId === currentPartner.partnerId);
        if (partnerToUpdate) {
          partnerToUpdate.partnerName = currentPartner.partnerName;
          partnerToUpdate.partnerCode = currentPartner.partnerCode;
          partnerToUpdate.uniqueId = currentPartner.uniqueId;
        }
      });
      resetLens(newPartners);
      client.writeQuery({
        query: LEASING_INCOME_SCHEMA_PARTNERS,
        variables: { id },
        data: { leasingIncomeSchemaXPartners: newPartners }
      });
      setPending(false);
      addToast(t('LeasingIncomeSchema.PartnersSaved'), 'success');
    }
  });

  const handleAdd = () => {
    const current = lens |> get;
    (lens |> set)([...current, { key: crypto.randomUUID(), id: null, schemaId: id }]);
  };

  const handleDelete = key => {
    const current = lens |> get;
    (lens |> set)(current.filter(item => item.key !== key));
  };

  const handleSave = () => {
    const data = lens |> get;
    if (!validate(data)) return;

    const sanitizedData = data.map(partner => {
      const sanitizedPartner = { ...partner };

      delete sanitizedPartner.key;
      delete sanitizedPartner.partnerName;
      delete sanitizedPartner.partnerCode;
      delete sanitizedPartner.uniqueId;

      return sanitizedPartner;
    });

    setPending(true);
    saveLeasingIncomeSchemaXPartners({
      variables: { schemaId: id, partners: sanitizedData }
    });
  };

  const hasItems = (lens.length |> get) > 0;
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);
  const disabled = useMemo(() => !canWrite || pending || !isEditable, [canWrite, pending, isEditable]);

  useEffect(() => {
    validate(lens |> get, dirtyInfo);
  }, [validate, lens, dirtyInfo]);
  useGlobalIsDirty(isDirty);

  return (
    <FlexBox direction='column' gap={1.5} mt={1.5}>
      <FlexBox justifyContent='end' gap={0.5} px={1}>
        <IconButton type='add' onClick={handleAdd} disabled={disabled} />
        <IconButton type='save' disabled={disabled || !isDirty} onClick={handleSave} />
      </FlexBox>
      <MaxHeightTable maxHeight={500}>
        <MaxHeightTable.Head>
          <RoundedHeaderTableRow>
            <ThemedHeaderTableCell>{t('LeasingIncomeSchema.PartnerName')}</ThemedHeaderTableCell>
            <ThemedHeaderTableCell>{t('LeasingIncomeSchema.PartnerCode')}</ThemedHeaderTableCell>
            <ThemedHeaderTableCell>{t('LeasingIncomeSchema.PartnerId')}</ThemedHeaderTableCell>
            <ThemedHeaderTableCell width='100px' />
          </RoundedHeaderTableRow>
        </MaxHeightTable.Head>
        <MaxHeightTable.Body>
          {loading && (
            <TableRow>
              <TableCell colSpan={3}>
                <WrappedFakeText lines={5} />
              </TableCell>
            </TableRow>
          )}
          {!loading &&
            hasItems &&
            (lens |> sequence).map((itemLens, index) => (
              <LeasingIncomeSchemaXPartnerComponent
                key={itemLens.key |> get}
                lens={itemLens}
                validation={validation[index]}
                onDelete={handleDelete}
                onOpenMasterPartner={handleOpenMasterPartner}
                canReadMasterPartner={canReadMasterPartner}
                disabled={disabled}
              />
            ))}
          {!loading && !hasItems && (
            <TableRow>
              <TableCell colSpan={3} align='center'>
                {t('LeasingIncomeSchema.NoPartners')}
              </TableCell>
            </TableRow>
          )}
        </MaxHeightTable.Body>
      </MaxHeightTable>
      {getErrors(validation) && (
        <ThemedWrapper color='error' flexDirection='column'>
          {getErrors(validation)
            .split(',')
            .map(error => (
              <span key={error}>{error}</span>
            ))}
        </ThemedWrapper>
      )}
    </FlexBox>
  );
};

LeasingIncomeSchemaXPartnersContainer.propTypes = {
  id: PropTypes.number
};

export default LeasingIncomeSchemaXPartnersContainer;
